import * as stateManager from '../state/stateManager';
import * as alertAction from '../state/actions/views/alertDialogue/alertAction';
import AlertPayload from '../state/actions/views/alertDialogue/alertPayload';
import * as modalSpinnerAction from '../state/actions/views/modalSpinnerDialogue/modalSpinnerAction';
import * as confirmAction from '../state/actions/views/confirmDialogue/confirmAction';
import ConfirmPayload from '../state/actions/views/confirmDialogue/confirmPayload';
import * as imageUploadDialogueAction from '../state/actions/views/imageUploadDialogue/imageUploadDialogueAction.1.0.0';
import * as modalFormAction from '../state/actions/views/modalForm/modalFormAction';
import ModalFormPayload from '../state/actions/views/modalForm/modalFormPayload';
import * as attachmentFormAction from '../state/actions/views/attachmentForm/attachmentFormAction';
import AttachmentFormPayload from '../state/actions/views/attachmentForm/attachmentFormPayload';
import * as stringUtil from '../string/stringUtil';

type AlertType = 'critical' | 'error' | 'success' | 'warning' | 'info' | null;

interface CloudinaryWidgetOptions {
    cloudName: string;
    uploadPreset: string;
    folder: string;
    cropping: boolean;
    croppingCoordinatesMode: string;
    showSkipCropButton: boolean;
    singleUploadAutoClose: boolean;
    multiple: boolean;
    showAdvancedOptions: boolean;
    showPoweredBy: boolean;
    resourceType: string;
    maxImageFileSize: number;
    maxImageWidth?: number;
    maxImageHeight?: number;
    minImageWidth?: number;
    minImageHeight?: number;
}

interface CloudinaryResult {
    event: string;
    info: {
        public_id: string;
    };
}

interface CloudinaryWidget {
    open: () => void;
}

declare global {
    interface Window {
        cloudinary: {
            createUploadWidget: (
                options: CloudinaryWidgetOptions,
                callback: (error: Error | null, result: CloudinaryResult | null) => void
            ) => CloudinaryWidget;
        };
    }
}

interface ModalFormOptions {
    confirmBtnTitle?: string;
    [key: string]: unknown;
}

interface OrgContext {
    franchisorId?: string;
    franchiseeId?: string;
    propertyId?: string;
}

export function showCriticalAlert(errorMessage: string): void {
    showAlert('critical', errorMessage + '');
}

export function showErrorAlert(errorMessage: string): void {
    showAlert('error', errorMessage + '');
}

export function showSuccessAlert(successMessage: string): void {
    showAlert('success', successMessage + '');
}

export function showWarningAlert(warningMessage: string): void {
    showAlert('warning', warningMessage + '');
}

export function showInfoAlert(infoMessage: string): void {
    showAlert('info', infoMessage + '');
}

export function showAlert(type: AlertType, message: string | null): void {
    if (stringUtil.isStringNullOrEmpty(message) || stringUtil.isStringNullOrEmpty(type)) {
        stateManager.getStore().dispatch(alertAction.createAction(null));
    }

    const alertPayload = new AlertPayload();
    alertPayload.type = type;
    alertPayload.message = message;

    stateManager.getStore().dispatch(alertAction.createAction(alertPayload));
}

export function clearAlert(): void {
    showAlert(null, null);
}

export function showSystemModalSpinner(waitMessage?: string | null): void {
    const message = stringUtil.isStringNullOrEmpty(waitMessage) ? null : waitMessage;
    stateManager.getStore().dispatch(modalSpinnerAction.createAction(message));
}

export function closeModalSpinner(): void {
    showSystemModalSpinner(null);
}

export function showImageUploadDialogue(
    title: string,
    imageUrl: string | null,
    imageCategory: string,
    onUpload: (imageUrl: string) => void,
    onRemove: () => void,
    onClose: () => void
): void {
    const imageUploadArgs = {
        title,
        imageUrl,
        imageCategory,
        onUpload,
        onRemove,
        onClose
    };
    stateManager.getStore().dispatch(imageUploadDialogueAction.createAction(imageUploadArgs));
}

export function removeUploadImage(): void {
    const currentState = stateManager.getStore().getState().imageUploadDialogue;
    stateManager.getStore().dispatch(
        imageUploadDialogueAction.createRemoveUploadedImageAction(currentState)
    );
}

export function updateUploadImage(imageUrl: string): void {
    const currentState = stateManager.getStore().getState().imageUploadDialogue;
    stateManager.getStore().dispatch(
        imageUploadDialogueAction.updateUploadImageAction(currentState, imageUrl)
    );
}

export function closeImageUploadDialogue(): void {
    stateManager.getStore().dispatch(imageUploadDialogueAction.createAction(null));
}

export function showConfirmDialogue(
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel: () => void
): void {
    const confirmPayload = new ConfirmPayload();
    confirmPayload.title = title;
    confirmPayload.message = message;
    confirmPayload.onConfirm = onConfirm;
    confirmPayload.onCancel = onCancel;

    stateManager.getStore().dispatch(confirmAction.createAction(confirmPayload));
}

export function closeConfirmDialogue(): void {
    stateManager.getStore().dispatch(confirmAction.createAction(null));
}

export function openModalForm(
    title: string,
    componentCreator: () => React.ReactNode,
    onSave: () => void,
    onValidate: () => string | boolean | null | undefined,
    isReadOnly: boolean,
    onDelete?: () => void,
    onCancel?: () => void,
    options: ModalFormOptions = { confirmBtnTitle: 'Save' }
): void {
    const modalFormPayload = new ModalFormPayload();
    modalFormPayload.title = title;
    modalFormPayload.componentCreator = componentCreator;
    modalFormPayload.onSave = onSave;
    modalFormPayload.onValidate = onValidate;
    modalFormPayload.isReadOnly = isReadOnly;
    modalFormPayload.onDelete = onDelete;
    modalFormPayload.onCancel = onCancel;
    modalFormPayload.options = options;

    stateManager.getStore().dispatch(modalFormAction.createAction(modalFormPayload));
}

export function closeModalForm(modalFormIndex: number): void {
    stateManager.getStore().dispatch(modalFormAction.createCloseAction(modalFormIndex));
}

export function openAttachmentForm(
    title: string,
    orgContexts: OrgContext,
    rootFolder: string,
    objectType: string,
    objectId: string,
    isReadOnly: boolean,
    onClose: () => void,
    files: unknown[]
): void {
    const attachmentFormPayload = new AttachmentFormPayload();
    attachmentFormPayload.title = title;
    attachmentFormPayload.orgContexts = orgContexts;
    attachmentFormPayload.rootFolder = rootFolder;
    attachmentFormPayload.objectType = objectType;
    attachmentFormPayload.objectId = objectId;
    attachmentFormPayload.onClose = onClose;
    attachmentFormPayload.isReadOnly = isReadOnly;
    attachmentFormPayload.files = files;

    stateManager.getStore().dispatch(attachmentFormAction.createAction(attachmentFormPayload));
}

export function closeAttachmentForm(): void {
    const attachmentFormPayload = new AttachmentFormPayload();
    attachmentFormPayload.title = null;
    attachmentFormPayload.objectType = null;
    attachmentFormPayload.objectId = null;
    attachmentFormPayload.onClose = null;
    attachmentFormPayload.isReadOnly = null;
    attachmentFormPayload.files = null;

    stateManager.getStore().dispatch(attachmentFormAction.createAction(attachmentFormPayload));
}

export function showImageUploadWidget(
    cloudName: string,
    imageFolder: string,
    uploadImageCallBack: (publicId: string) => void,
    maxImageSize: number = 2000000,
    maxWidth?: number,
    maxHeight?: number,
    minWidth?: number,
    minHeight?: number
): void {
    const widgetOptions: CloudinaryWidgetOptions = {
        cloudName: cloudName,
        uploadPreset: 'rms-jinoby-preset',
        folder: imageFolder,
        cropping: true,
        croppingCoordinatesMode: 'custom',
        showSkipCropButton: true,
        singleUploadAutoClose: false,
        multiple: false,
        showAdvancedOptions: false,
        showPoweredBy: false,
        resourceType: 'image',
        maxImageFileSize: maxImageSize
    };

    if (maxWidth) {
        widgetOptions.maxImageWidth = maxWidth;
        if (maxHeight) widgetOptions.maxImageHeight = maxHeight;
        if (minWidth) widgetOptions.minImageWidth = minWidth;
        if (minHeight) widgetOptions.minImageHeight = minHeight;
    }

    const widget = window.cloudinary.createUploadWidget(
        widgetOptions,
        (error: Error | null, result: CloudinaryResult | null) => {
            if (!error && result && result.event === 'success') {
                uploadImageCallBack(result.info.public_id);
            } else if (error) {
                console.error('Upload Widget Error:', error);
            }
        }
    );

    widget.open();
}

// Default export for backward compatibility with CommonJS imports
export default {
    showCriticalAlert,
    showErrorAlert,
    showSuccessAlert,
    showWarningAlert,
    showInfoAlert,
    showAlert,
    clearAlert,
    showSystemModalSpinner,
    closeModalSpinner,
    showImageUploadDialogue,
    removeUploadImage,
    updateUploadImage,
    closeImageUploadDialogue,
    showConfirmDialogue,
    closeConfirmDialogue,
    openModalForm,
    closeModalForm,
    openAttachmentForm,
    closeAttachmentForm,
    showImageUploadWidget
};
